// Forked from https://github.com/medplum/medplum/blob/main/packages/react/src/CcdaDisplay/CcdaDisplay.tsx
// Adapted to use @seen/react components and dependencies

import type { Attachment } from '@medplum/fhirtypes'
import { useMedplum } from '@seen/react'
import { Button } from '@seen/ui'
import { type JSX, useEffect, useRef, useState } from 'react'
import { exportJsonFile, sendCommand } from '#src/pages/ccda/utils.ts'

const CCDA_VIEWER_URL = 'https://ccda.medplum.com'
const BASE_VALIDATION_URL = 'https://ccda-validator.medplum.com/'

export interface CcdaDisplayProps {
  readonly attachment: Attachment
  readonly maxWidth?: number
  readonly height?: number
}

interface ValidationResult {
  resultsMetaData: {
    ccdaDocumentType: string
    ccdaVersion: string
    objectiveProvided: string
    serviceError: boolean
    serviceErrorMessage: string | null
    ccdaFileName: string
    ccdaFileContents: string
    resultMetaData: {
      type: string
      count: number
    }[]
    severityLevel: string
    totalConformanceErrorChecks: number
  }
}

export function CcdaDisplay(props: CcdaDisplayProps): JSX.Element | null {
  const { attachment, maxWidth, height = 400 } = props
  const url = attachment.url
  const medplum = useMedplum()
  const [shouldSend, setShouldSend] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [validationResult, setValidationResult] = useState<ValidationResult>()
  const [validating, setValidating] = useState(false)

  useEffect(() => {
    if (!url) {
      return
    }
    if (shouldSend && iframeRef.current) {
      sendCommand(iframeRef.current, {
        command: 'loadCcdaXml',
        value: url,
      }).catch(console.error)
      setShouldSend(false)
    }
  }, [url, shouldSend])

  const validateCcda = async (): Promise<void> => {
    if (!url) {
      return
    }

    try {
      setValidating(true)

      const blob = await medplum.downloadAttachmentAsBinary(url)
      const ccdaContent = await blob.text()

      const formData = new FormData()
      formData.append(
        'ccdaFile',
        new Blob([ccdaContent], { type: 'text/xml' }),
        'ccda.xml'
      )

      const validationUrl = `${BASE_VALIDATION_URL}referenceccdaservice/?validationObjective=C-CDA_IG_Plus_Vocab&referenceFileName=No%20scenario%20File&curesUpdate=true&severityLevel=WARNING`
      const validationResponse = await fetch(validationUrl, {
        method: 'POST',
        body: formData,
        credentials: 'omit',
        redirect: 'manual',
      })

      if (!validationResponse.ok) {
        throw new Error(
          `Validation failed: ${validationResponse.status} ${validationResponse.statusText}`
        )
      }

      const validationResult = await validationResponse.json()
      setValidationResult(validationResult as ValidationResult)
    } catch (error) {
      setValidationResult(undefined)
      console.error('CCDA validation error:', error)
    } finally {
      setValidating(false)
    }
  }

  const downloadResults = (): void => {
    if (!validationResult) {
      return
    }

    exportJsonFile(
      JSON.stringify(validationResult, null, 2),
      'ccda-validation-results'
    )
  }

  const getErrorCount = (): number => {
    if (!validationResult) {
      return 0
    }
    return validationResult.resultsMetaData.resultMetaData
      .filter((item) => item?.type.includes('Error'))
      .reduce((sum, item) => sum + (item.count || 0), 0)
  }

  if (!url) {
    return null
  }

  return (
    <div data-testid="ccda-iframe" style={{ maxWidth }}>
      <div style={{ minHeight: height }}>
        <iframe
          title="C-CDA Viewer"
          width="100%"
          height={height}
          ref={iframeRef}
          src={CCDA_VIEWER_URL}
          allowFullScreen={true}
          className="border-0"
          onLoad={() => setShouldSend(true)}
        />
      </div>

      <div className="mt-2.5 mb-2.5 flex items-center gap-4">
        <Button onClick={validateCcda} disabled={validating} size="sm">
          {validating ? 'Validating...' : 'Validate'}
        </Button>

        {validationResult && (
          <>
            <div>
              <strong>Validation Results:</strong> {getErrorCount()} errors
              found
            </div>

            <Button
              onClick={downloadResults}
              variant="outline"
              size="sm"
              className="ml-auto"
            >
              Download Full Results
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
